import React, { type ReactNode, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import ErrorIcon from '@atlaskit/icon/core/migration/error';

import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { getConfigureCustomFieldUrl } from '@atlassian/jira-custom-fields-common/src/common/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { isCMDBEnabledInEnvironment } from '@atlassian/jira-servicedesk-cmdb-fedramp/src/index.tsx';
import { getJsmChangeEditionUrl } from '@atlassian/jira-servicedesk-common/src/navigation/settings/index.tsx';
import { isPremiumInstance } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/resources/get-data/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import messages from './messages.tsx';

const UpgradeToPremiumLink = (chunks: ReactNode) => (
	<a href={getJsmChangeEditionUrl()} target="_blank">
		{chunks}
	</a>
);

export const InlineMessageContent = ({ children }: { children: React.ReactNode }) => (
	<InlineMessageContentWrapper>
		<IconWrapper>
			<ErrorIcon spacing="spacious" label="error" color={token('color.icon.danger')} />
		</IconWrapper>
		<div>{children}</div>
	</InlineMessageContentWrapper>
);

export const InvalidSchemaMessage = () => {
	const { formatMessage } = useIntl();
	// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
	return <p>{formatMessage(messages.errorInvalidFieldConfig)}</p>;
};

export const NotEnabledMessage = () => {
	const { formatMessage } = useIntl();
	// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
	return <p>{formatMessage(messages.errorInvalidFieldConfigDisabledCmdbAssets)}</p>;
};

export const InvalidSchemaAdminMessage = ({ fieldId }: { fieldId?: string }) => {
	const { formatMessage } = useIntl();
	return (
		<>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>{formatMessage(messages.errorInvalidFieldConfigAdmin)}</p>

			{fieldId && (
				<Button
					testId="servicedesk-cmdb-field-object-picker.ui.cmdb-field-error.button"
					appearance="link"
					href={getConfigureCustomFieldUrl(fieldId)}
					target="_blank"
				>
					{formatMessage(messages.errorInvalidFieldConfigAdminLink)}
				</Button>
			)}
		</>
	);
};

export const DowngradedMessage = () => {
	const { formatMessage } = useIntl();
	const notPremiumErrorAdmin = formatMessage(
		messages.errorInvalidFieldConfigDowngradedAdminCmdbAssets,
		{
			a: UpgradeToPremiumLink,
		},
	);
	return (
		<Heading size="xsmall" as="span">
			{notPremiumErrorAdmin}
		</Heading>
	);
};

export const FallbackError = () => {
	const { formatMessage } = useIntl();
	// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
	return <p>{formatMessage(messages.errorUnknown)}</p>;
};

export type Props = {
	fieldId?: string;
};

export const CmdbFieldError = (props: Props) => {
	const tenantContext = useTenantContext();
	const hasPremium = isPremiumInstance(tenantContext);
	const isCmdbEnabled = isCMDBEnabledInEnvironment(tenantContext.cmdbDisabledInFedRAMP);
	const { isAdmin } = tenantContext;

	const errorBody = useMemo(() => {
		if (!isCmdbEnabled) {
			return <NotEnabledMessage />;
		}

		if (hasPremium && !isAdmin) {
			return <InvalidSchemaMessage />;
		}

		if (hasPremium && isAdmin) {
			return <InvalidSchemaAdminMessage fieldId={props.fieldId} />;
		}

		if (!hasPremium && isAdmin) {
			return <DowngradedMessage />;
		}

		return <FallbackError />;
	}, [hasPremium, isAdmin, isCmdbEnabled, props.fieldId]);

	return <InlineMessageContent>{errorBody}</InlineMessageContent>;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span({
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineMessageContentWrapper = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& a, & span': {
		padding: '0 0 0 0',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		maxWidth: `${gridSize * 39}px`,
	},
});
